import React, { useMemo, useState, useEffect, useRef, Suspense, lazy } from "react";
import { supabase } from "./supabaseClient";
import { useLanguage } from "./LanguageContext";

// Components
import { OnboardingScreen } from "./components/OnboardingScreen";
import { NavButton } from "./components/NavButton";
import { AppIcon, UserIcon, MicIcon, BookIcon, AdminIcon } from "./components/icons";
import { PrivacyPolicy } from "./components/PrivacyPolicy";

// Utils
import { hasCompletedOnboarding } from "./utils/onboardingState";

// Logged-in-only screens (plus the post-login onboarding modal) are
// code-split out of the anonymous/homepage bundle: a first-time recruitment
// visitor who never signs in should not download TalkView's WebRTC voice
// pipeline, AdminView's ~1900 lines, or any of the rest of this — a static
// top-level import would have shipped it all regardless of whether MainApp
// ever rendered. Each `.then()` adapts the module's named export to the
// default export React.lazy() requires.
const TalkView = lazy(() =>
  import("./components/TalkView").then((m) => ({ default: m.TalkView }))
);
const AdminView = lazy(() =>
  import("./components/AdminView").then((m) => ({ default: m.AdminView }))
);
const AccountModal = lazy(() =>
  import("./components/AccountModal").then((m) => ({ default: m.AccountModal }))
);
const ConversationStartersView = lazy(() =>
  import("./components/ConversationStartersView").then((m) => ({ default: m.ConversationStartersView }))
);
const OnboardingModal = lazy(() =>
  import("./components/OnboardingModal").then((m) => ({ default: m.OnboardingModal }))
);

// --- Root Router ---
// Handles the /privacy route before any auth logic runs.
// No React Router dependency required — just checks window.location.pathname.
export default function SeniorFirstEnglishAssistant() {
  if (window.location.pathname === '/privacy') {
    return <PrivacyPolicy />;
  }
  return <AuthGate />;
}

// --- Auth Gate (extracted so hooks always run consistently) ---
function AuthGate() {
  const [userId, setUserId] = useState(null);
  // Default to the logged-out homepage, not a loading spinner: an anonymous
  // visitor (the overwhelming common case for this recruitment page) should
  // see real content immediately rather than wait on a Supabase round-trip
  // that will almost always just confirm "no session" anyway. `loading`
  // only ever flips true→false below; it's kept so the effect's two
  // `setLoading(false)` calls stay harmless no-ops instead of needing to be
  // torn out along with this.
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUserId(session?.user?.id ?? null);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
      setLoading(false);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-paper-50 text-ink-900">{t('app.loading')}</div>;
  }

  if (!userId) {
    return <OnboardingScreen onStart={(id) => setUserId(id)} />;
  }

  return <MainApp initialUserId={userId} />;
}

// --- Main App Component (logged in state) ---
function MainApp({ initialUserId }) {
  const [tab, setTab] = useState("talk"); // "talk" | "starters" | "admin"
  const [showOnboarding, setShowOnboarding] = useState(() => !hasCompletedOnboarding(initialUserId));
  const [contrast, setContrast] = useState(false);
  const [fontStep, setFontStep] = useState(1); // 0..2 for Small, Medium, Large
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  // sessionManager is dynamically imported (not a top-level static import)
  // so it ships in a lazy chunk instead of the main/anonymous bundle — see
  // the React.lazy() components above. It's fetched as soon as MainApp
  // mounts (i.e. right after login), well before a user could reach the
  // Talk tab, so saveTranscription/endSession are effectively always ready
  // by the time they're actually needed.
  const sessionManagerRef = useRef(null);
  useEffect(() => {
    import("./utils/sessionManager").then((mod) => {
      sessionManagerRef.current = mod;
    });
  }, []);

  // Navigating to Talk directly (header logo, bottom nav) is not the same as
  // arriving via a topic pick on Starters - clear any previously selected
  // topic so the ready screen doesn't keep confirming a stale topic the user
  // never chose this time around.
  const goToTalkTab = () => {
    setSelectedTopic(null);
    setTab("talk");
  };

  // Font size mappings for different elements based on fontStep
  const fontSizes = useMemo(() => {
    const sizes = [
      { base: "text-base", lg: "text-lg", xl: "text-xl", xxl: "text-2xl", xxxl: "text-3xl" },    // Small (0)
      { base: "text-lg", lg: "text-xl", xl: "text-2xl", xxl: "text-3xl", xxxl: "text-4xl" },     // Medium (1)
      { base: "text-xl", lg: "text-2xl", xl: "text-3xl", xxl: "text-4xl", xxxl: "text-5xl" }      // Large (2)
    ];
    return sizes[fontStep];
  }, [fontStep]);

  // "Wine & Stone" tokens. Dark mode is a purpose-built surface/text pair
  // (surface-dark #2E2C38 / surface-dark-text #F1F0F3), not an inversion of
  // the light tokens — see tailwind.config.js. `dark` class here plus
  // `darkMode: "class"` in tailwind.config.js is what actually makes every
  // `dark:` utility already used throughout the app respond to this toggle;
  // previously the "dark" class name did nothing because the config had no
  // darkMode strategy set (defaulted to OS-level "media").
  const theme = contrast ? "dark bg-surface-dark text-surface-dark-text" : "bg-paper-50 text-ink-900";
  const cardTheme = contrast ? "bg-surface-dark border-ink-600" : "bg-paper-100 border-ink-200";
  const subtleText = contrast ? "text-ink-400" : "text-ink-600";
  const headerTheme = contrast ? "bg-surface-dark/80 backdrop-blur" : "bg-paper-50/80 backdrop-blur";
  const activeNavText = contrast ? "text-surface-dark-text" : "text-indigo-600";
  const inactiveNavText = contrast ? "text-ink-400" : "text-ink-400";

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error('Error logging out:', error.message);
  };

  // Load user information and avatar
  const loadUserInfo = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (user) {
      setUserInfo({
        email: user.email,
        id: user.id,
        createdAt: new Date(user.created_at).toLocaleDateString()
      });

      // Load profile data including avatar and admin status
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('avatar_url, is_admin')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error loading profile:', profileError);
      } else if (profile) {
        // Always update avatar URL, even if empty
        setAvatarUrl(profile.avatar_url || '');
        setIsAdmin(profile.is_admin || false);
        console.log('Profile loaded successfully:', { avatar_url: profile.avatar_url, is_admin: profile.is_admin });
      } else {
        console.warn('No profile found for user:', user.id);
      }
    }
  };

  useEffect(() => {
    loadUserInfo();
  }, []);

  // Debug: Log when avatarUrl changes
  useEffect(() => {
    console.log('MainApp avatarUrl state updated:', avatarUrl);
  }, [avatarUrl]);

  useEffect(() => {
    // End session when user closes browser. sessionManager may not have
    // finished loading yet (see the dynamic import above) — guarded with
    // `?.` the same way its absence was already implicitly tolerated
    // before (calling a not-yet-relevant endSession was always a no-op:
    // it bails out immediately when there's no active session).
    const handleUnload = () => sessionManagerRef.current?.endSession();
    window.addEventListener('beforeunload', handleUnload);

    // Cleanup function
    return () => {
      sessionManagerRef.current?.endSession();
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  useEffect(() => {
    // Log tab switches or other significant events
    if (tab) {
      console.log(`User navigated to tab: ${tab}`);
    }
  }, [tab]);

  return (
    <div className={`min-h-screen flex flex-col ${theme} ${fontSizes.base}`}>
      <header className={`sticky top-0 z-10 border-b ${contrast ? 'border-ink-600' : 'border-ink-200'} ${headerTheme}`}>
        <div className="mx-auto max-w-5xl px-4 py-3 flex items-center justify-between">
          <button
            onClick={goToTalkTab}
            className="flex items-center gap-3 hover:opacity-80 transition"
            aria-label="Go to Talk"
          >
            <AppIcon />
            <div className="leading-tight">
              <div className="font-bold text-lg md:text-xl">{t('app.title')}</div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-2 rounded-control border text-sm font-semibold ${cardTheme} hover:opacity-80 transition-opacity`}
              onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
              aria-label="Change language"
            >
              {language === 'es' ? '🇬🇧 EN' : '🇪🇸 ES'}
            </button>
            <button
              className={`px-4 py-2 rounded-control border text-sm font-semibold ${cardTheme} hover:opacity-80 transition-opacity`}
              onClick={() => setContrast(v => !v)} aria-pressed={contrast}
            >
              {contrast ? t('common.lightMode') : t('common.darkMode')}
            </button>
            <div className={`flex items-center rounded-control border ${cardTheme}`}>
              <button
                className="px-4 py-2 font-bold text-lg hover:opacity-80 disabled:opacity-30"
                onClick={() => setFontStep(s => Math.max(0, s - 1))}
                aria-label="Decrease text size" disabled={fontStep === 0}
              >A-</button>
              <div className={`w-px h-6 ${contrast ? 'bg-ink-600' : 'bg-ink-200'}`}></div>
              <button
                className="px-4 py-2 font-bold text-lg hover:opacity-80 disabled:opacity-30"
                onClick={() => setFontStep(s => Math.min(2, s + 1))}
                aria-label="Increase text size" disabled={fontStep === 2}
              >A+</button>
            </div>
            <button className={`w-12 h-12 rounded-full grid place-items-center ${cardTheme} border overflow-hidden`} aria-label="Profile" onClick={() => setShowAccountModal(true)}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error('Failed to load avatar:', avatarUrl);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => console.log('Avatar loaded successfully:', avatarUrl)}
                />
              ) : (
                <UserIcon />
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8">
        <Suspense fallback={<div className={`text-center ${fontSizes.base} ${subtleText}`}>{t('app.loading')}</div>}>
          {tab === "talk" && (
            <TalkView
              subtleText={subtleText}
              cardTheme={cardTheme}
              fontSizes={fontSizes}
              onSaveTranscription={(text, correctedText) => sessionManagerRef.current?.saveTranscription(text, correctedText)}
              selectedTopic={selectedTopic}
            />
          )}
          {tab === "starters" && <ConversationStartersView cardTheme={cardTheme} subtleText={subtleText} fontSizes={fontSizes} onStartConversation={(topic) => {
            setSelectedTopic(topic);
            setTab("talk");
          }} />}
          {tab === "admin" && isAdmin && <AdminView cardTheme={cardTheme} subtleText={subtleText} fontSizes={fontSizes} contrast={contrast} />}
        </Suspense>
      </main>

      {/* Jakob's Law: Bottom tab bar is a familiar navigation pattern for mobile users. */}
      <nav className={`sticky bottom-0 border-t ${contrast ? 'border-ink-600' : 'border-ink-200'} ${headerTheme}`}>
        <div className="mx-auto max-w-5xl px-4">
          <div className={`grid ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'} gap-2 py-2`}>
            <NavButton active={tab === "starters"} onClick={() => setTab("starters")} label={t('nav.starters')} icon={<BookIcon active={tab === 'starters'} />} activeColor={activeNavText} inactiveColor={inactiveNavText} />
            <NavButton active={tab === "talk"} onClick={goToTalkTab} label={t('nav.talk')} icon={<MicIcon active={tab === 'talk'} />} activeColor={activeNavText} inactiveColor={inactiveNavText} />
            {isAdmin && <NavButton active={tab === "admin"} onClick={() => setTab("admin")} label={t('nav.admin')} icon={<AdminIcon active={tab === 'admin'} />} activeColor={activeNavText} inactiveColor={inactiveNavText} />}
          </div>
        </div>
      </nav>

      {/* Onboarding modal — shown once per user after first login, keyed by
          Supabase user ID. fallback={null}: both modals below are already
          gated behind a user action (login, tapping the profile icon) that
          gives their lazy chunk a head start loading, so a brief unstyled
          gap is preferable to a flash of placeholder UI. */}
      {showOnboarding && (
        <Suspense fallback={null}>
          <OnboardingModal userId={initialUserId} onComplete={() => setShowOnboarding(false)} />
        </Suspense>
      )}

      {/* Account Modal */}
      {showAccountModal && (
        <Suspense fallback={null}>
          <AccountModal
            userInfo={userInfo}
            onClose={() => setShowAccountModal(false)}
            onLogout={handleLogout}
            onSave={loadUserInfo}
            theme={theme}
            cardTheme={cardTheme}
            subtleText={subtleText}
            currentAvatarUrl={avatarUrl}
          />
        </Suspense>
      )}
    </div>
  );
}
